import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import ColumnConfigurator from './ColumnConfigurator';
import FilterConfigurator from './FilterConfigurator';

export interface Column {
  fieldName: string;
  caption: string;
  width: number;
  isVisible: boolean;
  isRequired: boolean;
}

export interface Filter {
  fieldName: string;
  caption: string;
  editor: string;
  isVisible: boolean;
  isRequired: boolean;
}

export interface SortConfig {
  fieldName: string | null;
  direction: 'ascending' | 'descending' | null;
}

interface GridConfiguratorProps {
  isAdmin: boolean;
  tableName: string,
  availableFields: string[];
  availableFilters: string[];
  initialColumns?: Column[];
  initialFilters?: Filter[];
  initialSort?: SortConfig;
}

const GridConfigurator: React.FC<GridConfiguratorProps> = (
  {
    isAdmin,
    tableName,
    availableFields,
    availableFilters,
    initialColumns = [],
    initialFilters = [],
    initialSort = { fieldName: null, direction: null },
  }) => {
  const [isOpen, setIsOpen] = useState<boolean>();
  const [columns, setColumns] = useState<Column[]>(initialColumns);
  const [filters, setFilters] = useState<Filter[]>(initialFilters);
  const [sortConfig, setSortConfig] = useState<SortConfig>(initialSort);
  const [activeTab, setActiveTab] = useState<string>('columns');

  const dialogRef = useRef<HTMLDialogElement>(null);

  const openConfig = () => {
    setIsOpen(true);
    dialogRef.current?.showModal();
  };

  const isSaveDisabled = filters.some(col => !col.fieldName || !col.caption) || columns.some(col => !col.fieldName || !col.caption);

  const handleSave = async () => {
    try {
      const response = await fetch('/api/grid/setColumns/' + tableName, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          columns,
          filters,
          ...(sortConfig.fieldName ? { sortOrder: sortConfig } : {}),
        }),
      });

      if (response.ok) {
        dialogRef.current?.close();
        window.location.reload();
      } else {
        console.error('Error while saving:', response.statusText);
        alert('Failed to save configuration. Please try again.');
      }
    } catch (error) {
      console.error('Error while saving:', error);
      alert('Failed to save configuration. Please try again.');
    }
  };

  const handleColumnChange = async (newColumns: Column[], newSortConfig: SortConfig) => {
    setColumns(newColumns);
    setSortConfig(newSortConfig);
  };

  const handleFilterChange = async (newFilters: Filter[]) => setFilters(newFilters);

  const handleCancel = () => {
    setIsOpen(false);
    dialogRef.current?.close();
  };

  return <>
    <a type="button" onClick={openConfig} title={window.ifunds.strings.configureGrid}>
      <i className="icons8-settings"></i>
    </a>
    {createPortal(
      <dialog ref={dialogRef} className="modal">
        {isOpen ?
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title">{window.ifunds.strings.configureGrid}</h1>
              <button type="button" className="btn-close" aria-label="Close" onClick={handleCancel}></button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto' }}>
              {filters.length > 0 && (
                <ul className="nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'columns' ? 'active' : ''}`}
                      onClick={() => setActiveTab('columns')}
                    >
                      Columns
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'filters' ? 'active' : ''}`}
                      onClick={() => setActiveTab('filters')}
                    >
                      Filters
                    </button>
                  </li>
                </ul>
              )}
              <div className={`${activeTab === 'columns' ? 'd-block' : 'd-none'}`}>
                <ColumnConfigurator
                  isAdmin={isAdmin}
                  availableFields={availableFields}
                  columns={columns}
                  sortConfig={sortConfig}
                  onColumnChange={handleColumnChange}
                />
              </div>
              <div className={`${activeTab === 'filters' ? 'd-block' : 'd-none'}`}>
                <FilterConfigurator
                  isAdmin={isAdmin}
                  availableFilters={availableFilters}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaveDisabled}>
                {window.ifunds.strings.save}
              </button>
              <button type="button" className="btn btn-outline-primary me-2" onClick={handleCancel}>
                {window.ifunds.strings.cancel}
              </button>
            </div>
          </div>
          : undefined
        }
      </dialog>,
      document.body
    )}
  </>;
};

export default function registerGridConfigurator(
  isAdmin: boolean,
  tableName: string,
  availableFields: string[],
  availableFilters: string[],
  initialColumns?: Column[],
  initialFilters?: Filter[],
  initialSort?: SortConfig
) {
  const container = document.getElementById('grid-configurator');
  if (container) {
    createRoot(container).render(
      <GridConfigurator
        isAdmin={isAdmin}
        tableName={tableName}
        availableFields={availableFields}
        availableFilters={availableFilters}
        initialColumns={initialColumns}
        initialFilters={initialFilters}
        initialSort={initialSort}
      />
    );
  }
}
